import { lazy, type ComponentType } from 'react';

/**
 * Wraps React.lazy with automatic retry on chunk load failures.
 * After a deploy, users with cached index.html reference old chunk hashes.
 * On failure, a single page reload fetches the new index.html with correct hashes.
 * Uses sessionStorage to prevent infinite reload loops.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
) {
  return lazy(() =>
    importFn().catch((error: Error) => {
      const isChunkError =
        error.message.includes('dynamically imported module') ||
        error.message.includes('Failed to fetch') ||
        error.message.includes('Loading chunk') ||
        error.message.includes('Loading CSS chunk') ||
        error.message.includes('Importing a module script failed');

      if (isChunkError) {
        const reloadKey = 'chunk_reload';
        if (!sessionStorage.getItem(reloadKey)) {
          sessionStorage.setItem(reloadKey, '1');
          window.location.reload();
        }
      }

      throw error;
    })
  );
}
